import { useEffect, useRef, useState } from "react";
import WaveSurfer from "wavesurfer.js";
import { useWavesurferContext } from "@/hooks/providers/wavesurfer-provider";

export const useWaveSurfer = (url: string, isLazyLoading: boolean = false) => {
  const waveformRef = useRef(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [waveSurfer, setWaveSurfer] = useState<WaveSurfer | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isItemLoading, setIsItemLoading] = useState(false);
  const [audioCache, setAudioCache] = useState<{ [url: string]: Blob }>({});
  const [isAudioLoaded, setIsAudioLoaded] = useState(false);

  const loadAudio = async (audioUrl: string, ws: WaveSurfer) => {
    if (!ws) {
      return;
    }

    setIsItemLoading(true);

    try {
      let blob: Blob;

      if (audioCache[audioUrl]) {
        blob = audioCache[audioUrl];
      } else {
        const url = new URL(audioUrl);

        if (url.protocol === "blob:") {
          blob = await fetch(audioUrl).then((r) => r.blob());
        } else {
          const response = await fetch(
            `/api/split/wave-suffer?url=${encodeURIComponent(audioUrl)}`,
          );
          if (!response.ok) {
            throw new Error("Failed to fetch audio file");
          }

          const arrayBuffer = await response.arrayBuffer();
          blob = new Blob([arrayBuffer], { type: "audio/mpeg" });
        }

        setAudioCache((prev) => ({ ...prev, [audioUrl]: blob }));
      }

      await ws.loadBlob(blob);
      setIsAudioLoaded(true);
    } catch (error) {
      console.error("Error loading audio:", error);
      setIsAudioLoaded(false);
    } finally {
      setIsItemLoading(false);
    }
  };

  const context = useWavesurferContext();

  const togglePlay = async () => {
    if (isLazyLoading && !isAudioLoaded) {
      if (!waveSurfer) {
        return;
      }

      await loadAudio(url, waveSurfer);
    }

    context.togglePlay(url);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  useEffect(() => {
    setIsPlaying(context.playingUrl === url);
  }, [context.playingUrl, url]);

  useEffect(() => {
    const handleChange = async () => {
      if (isPlaying) {
        await waveSurfer?.play();
      } else {
        waveSurfer?.pause();
      }
    };

    handleChange();
  }, [isPlaying]);

  useEffect(() => {
    setIsAudioLoaded(false);

    if (!waveformRef.current || !url) {
      return;
    }

    const ws = WaveSurfer.create({
      container: waveformRef.current,
      fillParent: true,
      waveColor: "#555",
      progressColor: "#ff5722",
      height: 24,
      barGap: 2,
      cursorColor: "#ff5722",
    });

    if (!isLazyLoading) {
      loadAudio(url, ws);
    }

    ws.on("finish", () => {
      setIsPlaying(false);
      ws.seekTo(0);
      ws.pause();
    });

    ws.on("ready", () => {
      setIsItemLoading(false);
      setDuration(ws.getDuration());
    });

    ws.on("audioprocess", () => {
      setCurrentTime(ws.getCurrentTime());
    });

    setWaveSurfer(ws);

    return () => {
      ws.destroy();
    };
  }, [url]);

  return {
    waveformRef,
    currentTime,
    duration,
    formatTime,
    isItemLoading,
    waveSurfer,
    isPlaying,
    togglePlay,
  };
};
